<!--
 * @Description: 开票类目分组-单分组项 — 申请开票时单个分组的类目选择 + 选择开票数据行
 *               每个分组独立选择开票类目和结算数据；已选数据跨分组排除由容器统一下发
 *               @ImportBy: ./individual-category-group.vue（容器）
 * @Date: 2026-06-03
-->
<template>
  <div class="invoice-category-group">
    <div class="group-header">
      <span class="group-title">开票类目 {{ groupIndex + 1 }}</span>
      <BaseButton
        v-if="groupIndex > 0 && !disabled"
        type="text"
        style="color: #fa4d56"
        @click="$emit('remove', groupIndex)"
      >
        删除
      </BaseButton>
    </div>

    <!-- 开票类目选择 -->
    <div class="group-row">
      <span class="group-label">开票类目</span>
      <BaseSelect
        class="group-select"
        placeholder="请选择"
        label-key="itemName"
        value-key="id"
        :init="true"
        :remoteMethod="categoryRemote"
        :disabled="disabled || !formData.invoiceType"
        v-model="localData.itemId"
        @change="onCategoryChange"
      />
    </div>

    <!-- 选择开票数据 -->
    <div class="group-row" v-if="!disabled">
      <span class="group-label">开票数据</span>
      <BaseButton
        type="text"
        @click="onOpenSelectData"
        :disabled="!canSelectData"
      >
        选择开票数据
      </BaseButton>
    </div>

    <!-- 已选数据摘要 + 表格 -->
    <div class="group-data-summary" v-if="selectedData.length">
      <div class="summary-row">
        <span>
          已选
          <b>{{ selectedData.length }}</b>
          条
        </span>
        <span style="color: #e97132">
          申请开票金额：¥{{ formatter.fen2yuan(selectedTotalAmount) }}
        </span>
      </div>
      <BaseTable
        class="group-table"
        :data="selectedData"
        :columns="summaryColumns"
        :pagination="false"
        size="small"
      />
    </div>

    <div class="group-empty" v-else-if="!disabled">暂未选择开票数据</div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, getCurrentInstance } from 'vue';
import {
  BaseButton,
  BaseSelect,
  BaseTable,
  useCreateModal,
  useTableColumn
} from 'xrk-components';
import { formatter } from '@/utils';
import { invoiceCategory } from '@V/invoice/src/data-source/apis';
import type { InvoiceType } from '@V/invoice/src/types';
import InvoiceSelectDataDialog from '../invoice-select-data-dialog/invoice-select-data-dialog.vue';
import type {
  CategoryGroups,
  IndividualNotInvoicedPageRespDTO
} from '@V/invoice/src/data-source/types';
import { SETTLEMENT_STATUS_OPTIONS } from '@V/invoice/src/constants/index';

const props = withDefaults(
  defineProps<{
    groupIndex: number;
    formData: {
      theSellerId?: number | null;
      invoiceType?: number | null;
      payPassagewayCode?: string;
      individualId?: number | null;
    };
    itemId?: number | null;
    settlementList?: any[];
    /** 跨组全局已选 settlementUserTaskId（传给弹窗做排除） */
    globalPreSelectedIds?: number[];
    disabled?: boolean;
  }>(),
  {
    itemId: null,
    settlementList: () => [],
    globalPreSelectedIds: () => [],
    disabled: false,
    formData: () => ({})
  }
);

const emits = defineEmits<{
  (event: 'remove', index: number): void;
  (event: 'update:data', value: CategoryGroups[0]): void;
}>();

const localData = ref<{ itemId: number | null; itemName: string }>({
  itemId: null,
  itemName: ''
});

const selectedData = ref<IndividualNotInvoicedPageRespDTO[]>([]);

const canSelectData = computed(
  () =>
    props.formData?.theSellerId != null &&
    props.formData?.invoiceType != null &&
    // !!props.formData?.payPassagewayCode && // 后端要求去掉必选
    localData.value.itemId != null
);

const selectedTotalAmount = computed(() => {
  return selectedData.value.reduce((sum, item) => {
    return sum + (item.terminalSettlementAmount || 0);
  }, 0);
});

const summaryColumns = computed(() => [
  useTableColumn({
    label: '结算单号',
    prop: 'settlementNo',
    width: '160px'
  }),
  useTableColumn({
    label: '项目名称',
    prop: 'projectName',
    minWidth: '120px'
  }),
  useTableColumn({
    label: '工业名称',
    prop: 'industryName',
    width: '140px'
  }),
  useTableColumn({
    label: '个体户名称',
    prop: 'individualName',
    width: '130px'
  }),
  useTableColumn({
    label: '终端结算金额(元)',
    prop: 'terminalSettlementAmount',
    width: '160px',
    formatter: ({ cellValue }) => formatter.fen2yuan(cellValue)
  }),
  useTableColumn({
    label: '结算单确认时间',
    prop: 'settlementConfirmTime',
    minWidth: '150px',
    formatter: ({ cellValue }) => formatter.date(cellValue)
  }),
  useTableColumn({
    label: '结算单状态',
    prop: 'settlementStatus',
    width: '100px',
    formatter: ({ cellValue }) =>
      formatter.defaultFormat(
        cellValue,
        () =>
          SETTLEMENT_STATUS_OPTIONS.find(o => o.value === cellValue)?.label ||
          '-'
      )
  })
]);

const categoryRemote = () => {
  return invoiceCategory({
    stateType: 7,
    invoiceType: props.formData?.invoiceType as InvoiceType,
    theSellerId: props.formData?.theSellerId,
    _t: props.groupIndex
  });
};

const onCategoryChange = (_val: any, [option]: any[]) => {
  localData.value.itemName = option?.itemName || '';
  emitUpdate();
};

const onOpenSelectData = () => {
  useCreateModal(
    InvoiceSelectDataDialog,
    {
      type: 1,
      globalPreSelectedIds: props.globalPreSelectedIds,
      currentPreSelectedIds: selectedData.value.map(
        (item: any) => item.settlementUserTaskId
      ),
      individualId: props.formData?.individualId,
      onConfirm(rows: any[]) {
        selectedData.value = rows;
        emitUpdate();
      }
    },
    getCurrentInstance()
  );
};

const emitUpdate = () => {
  emits('update:data', {
    itemId: localData.value.itemId,
    itemName: localData.value.itemName,
    settlementList: selectedData.value
  });
};

watch(
  () => [props.formData?.invoiceType, props.formData?.theSellerId],
  () => {
    localData.value.itemId = null;
    localData.value.itemName = '';
    selectedData.value = [];
  }
);

watch(
  () => [props.itemId, props.settlementList],
  () => {
    localData.value.itemId = props.itemId ?? null;
    selectedData.value = props.settlementList ? [...props.settlementList] : [];
  },
  { immediate: true }
);

defineExpose({
  getData: () => ({
    itemId: localData.value.itemId,
    itemName: localData.value.itemName,
    settlementList: selectedData.value
  })
});
</script>

<style lang="scss" scoped>
.invoice-category-group {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 16px;
}

.group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 8px;
}

.group-title {
  font-weight: 600;
  font-size: 15px;
}

.group-row {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.group-label {
  width: 100px;
  flex-shrink: 0;
  color: #666;
}

.group-select {
  width: 280px;
}

.group-data-summary {
  background: #fafafa;
  padding: 10px 12px;
  border-radius: 4px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.group-table {
  margin-top: 4px;
}

.group-empty {
  color: #ccc;
  font-size: 13px;
  padding: 8px 0;
}
</style>
